import { Directive, ElementRef, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { ErrorDetail } from './errors-details/errors-details.component';
import { ValidationErrorsService } from './validation-errors-service';

@Directive({ selector: '[avanoValidationErrorSupport]' })
export class AvanoValidationErrorSupportDirective implements OnInit, OnDestroy {
  private destroyed$ = new Subject();
  private readonly key = uuidv4();

  @Input() avanoValidationErrorLabel: string;
  @Input() set avanoValidationErrorSupport(control: AbstractControl) {
    this.formControl = control;
    this.ngOnInit();
  }

  formControl: AbstractControl;

  private _breadcrumb: string[];
  get breadcrumb(): string[] {
    this._breadcrumb = this._breadcrumb ?? this.buildBreadcrumb();
    return this._breadcrumb;
  }

  constructor(private elementRef: ElementRef, @Optional() private validationService: ValidationErrorsService) {}

  ngOnInit(): void {
    if (!this.validationService || !this.formControl || !this.avanoValidationErrorLabel) return;
    this.updateValidationError(this.formControl.status);
    this.formControl.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (statusChange) => {
        this.updateValidationError(statusChange);
      }
    });
  }

  ngOnDestroy(): void {
    this.validationService.clearValidationError(this.key);

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  buildBreadcrumb(): string[] {
    const element = this.elementRef.nativeElement as HTMLElement;
    const breadcrumb = [];
    let finished = false;
    let parent = element.parentElement;
    while (!finished && !!parent) {
      const parentBreadcrumb = parent.attributes.getNamedItem(ValidationErrorsService.attributeName);
      if (parentBreadcrumb) {
        breadcrumb.unshift(parentBreadcrumb.value);
      }
      finished = parent.tagName === 'avano-panel';
      parent = parent.parentElement;
    }

    return breadcrumb;
  }

  private updateValidationError(newStatus: any) {
    if (this.formControl.pristine === true) {
      if (this.validationService.currentErrors.has(this.key)) {
        this.validationService.clearValidationError(this.key);
      }
      return;
    }
    const touchedOrDirty = this.formControl.touched || this.formControl.dirty;
    if (touchedOrDirty && newStatus === 'INVALID') {
      const errorDetails: ErrorDetail = {
        title: [...this.breadcrumb].join(' - '),
        description: `${this.avanoValidationErrorLabel}: ${this.validationService.getErrorDescriptionText(this.formControl.errors)}`,
        source: {
          element: this.elementRef.nativeElement,
          name: this.avanoValidationErrorLabel
        }
      };
      this.validationService.addValidationError(this.key, errorDetails);
    } else {
      this.validationService.clearValidationError(this.key);
    }
  }
}
